import React, { useEffect, useCallback, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useAppSelector, useAppDispatch } from '../../hooks';
import { fetchProfile, acceptTerms } from '../../store/slices';
import { TermsModal } from '../common';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { profile, error: profileError } = useAppSelector((state) => state.profile);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [profileFetched, setProfileFetched] = useState(false);
  const [fetchSucceeded, setFetchSucceeded] = useState(false);

  // Fetch profile when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProfile())
        .unwrap()
        .then(() => {
          setFetchSucceeded(true);
        })
        .catch((error) => {
          console.error('[ProtectedRoute] Profile fetch failed:', error);
          setFetchSucceeded(false);
        })
        .finally(() => {
          setProfileFetched(true);
        });
    }
  }, [isAuthenticated, dispatch]);

  // Derive termsAccepted from profile - only after profile has been fetched SUCCESSFULLY
  // If profile fetch failed (e.g., 401), we should NOT show the terms modal - let auth flow handle it
  // termsAccepted is:
  //   - null: profile not fetched yet (show loading)
  //   - true: profile fetched and terms are accepted
  //   - false: profile fetched successfully but terms NOT accepted (show modal)
  const termsAccepted = profileFetched 
    ? (fetchSucceeded ? !!profile?.termsAcceptedAt : true) // If fetch failed, treat as "accepted" to avoid showing modal
    : null;
  
  console.log('[ProtectedRoute] profileFetched:', profileFetched);
  console.log('[ProtectedRoute] fetchSucceeded:', fetchSucceeded);
  console.log('[ProtectedRoute] profile:', profile);
  console.log('[ProtectedRoute] profile.termsAcceptedAt:', profile?.termsAcceptedAt);
  console.log('[ProtectedRoute] termsAccepted:', termsAccepted);
  console.log('[ProtectedRoute] profileError:', profileError);

  const handleAcceptTerms = useCallback(async () => {
    setIsAcceptingTerms(true);
    try {
      await dispatch(acceptTerms()).unwrap();
      // Profile will be updated in the store with termsAcceptedAt, which will update termsAccepted
    } catch (error) {
      console.error('Failed to accept terms:', error);
    } finally {
      setIsAcceptingTerms(false);
    }
  }, [dispatch]);

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading while fetching profile initially
  if (!profileFetched) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading profile...
      </div>
    );
  }

  return (
    <>
      {children}
      <TermsModal 
        isOpen={termsAccepted === false} 
        onAccept={handleAcceptTerms}
        isLoading={isAcceptingTerms}
      />
    </>
  );
};
